import { formatDateOnly } from "Infrastructure/Helpers/dateFormatters";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, useTheme } from "@mui/material";
import { addDays, max, parseISO, startOfDay } from "date-fns";
import { useForm, useWatch } from "react-hook-form";
import { useCreateOrUpdateQuestMutation } from "../../../../Quests/Queries/useCreateOrUpdateQuestMutation";
import { type QuestDetail, QuestState, QuestType } from "../../../Api/Api";
import { SideDrawer } from "../../Drawers/SideDrawer";
import { CloseButton } from "../Buttons/CloseButton";
import { SubmitButton } from "../Buttons/SubmitButton";
import { FormDatePickerInput } from "../Inputs/FormDatePickerInput";
import { FormNumberInput } from "../Inputs/FormNumberInput";
import { FormRichTextInput } from "../Inputs/FormRichTextInput";
import { FormSelectInput } from "../Inputs/FormSelectInput";
import { FormTextInput } from "../Inputs/FormTextInput";
import {
  type QuestFormData,
  type QuestFormInput,
  questSchema,
} from "../Schemas/questSchema";

interface CreateQuestFormProps {
  mode?: "create" | "edit";
  quest?: QuestDetail | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const parseDateOnly = (dateString: string): Date =>
  startOfDay(parseISO(dateString));

export const CreateQuestForm = ({
  mode = "create",
  quest,
  open,
  onClose,
  onSuccess,
}: CreateQuestFormProps) => {
  const { colors, customSpacing, radius } = useTheme();
  const { t } = useResource();
  const { mutate: createOrUpdateQuest, isPending } =
    useCreateOrUpdateQuestMutation();

  const getDefaultValues = (): QuestFormInput => {
    if (mode === "edit" && quest) {
      return {
        title: quest.name,
        reward: quest.rewardCoins,
        start: quest.dateStart ? parseDateOnly(quest.dateStart) : undefined,
        expiration: quest.dateExpiration
          ? parseDateOnly(quest.dateExpiration)
          : undefined,
        description: quest.description || undefined,
        type: quest.type,
        maxParticipants: quest.maxParticipants || null,
      };
    }

    return {
      title: "",
      reward: 0,
      start: undefined,
      expiration: undefined,
      description: undefined,
      type: QuestType.BugHunter,
      maxParticipants: null,
    };
  };

  const { control, handleSubmit, reset } = useForm<QuestFormInput>({
    resolver: zodResolver(questSchema),
    defaultValues: getDefaultValues(),
  });

  const watchedStartDate = useWatch({
    control,
    name: "start",
  });

  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);

  const isStartDateInPast = Boolean(
    mode === "edit" && quest?.dateStart && new Date(quest.dateStart) < today,
  );

  const minExpirationDate = watchedStartDate
    ? max([tomorrow, addDays(watchedStartDate, 1)])
    : tomorrow;

  const handleFormSubmit = handleSubmit((data) => {
    const formData = data as QuestFormData;
    const commandData = {
      questID: mode === "edit" && quest ? quest.questID : null,
      name: formData.title,
      type: formData.type,
      description: formData.description || undefined,
      rewardXP: formData.reward || 0,
      rewardCoins: formData.reward || 0,
      state: QuestState.Active,
      maxParticipants: formData.maxParticipants || null,
      dateStart: formData.start
        ? formatDateOnly(formData.start)
        : formatDateOnly(new Date()),
      dateExpiration: formData.expiration
        ? formatDateOnly(formData.expiration)
        : null,
    };

    createOrUpdateQuest(commandData, {
      onSuccess: () => {
        reset();
        onClose();
        onSuccess?.();
      },
    });
  });

  const handleClose = () => {
    if (!isPending) {
      reset();
      onClose();
    }
  };

  return (
    <SideDrawer
      open={open}
      onClose={handleClose}
      title={
        mode === "create"
          ? t(Resources.Pages.CreateQuest.Title)
          : t(Resources.Pages.EditQuest.Title)
      }
      disabled={isPending}
    >
      <Stack
        component="form"
        onSubmit={handleFormSubmit}
        sx={{
          flex: 1,
          minHeight: 0,
        }}
        spacing={customSpacing.xl}
      >
        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
          }}
          spacing={customSpacing.md}
        >
          <Stack
            sx={{
              backgroundColor: colors.background.primary,
              borderRadius: radius.xl,
              p: customSpacing.xl,
            }}
            spacing={customSpacing.xl}
          >
            <Stack direction="row" spacing={customSpacing.md}>
              <Box sx={{ flex: 6.5 }}>
                <FormTextInput
                  name="title"
                  control={control}
                  label={t(Resources.Pages.CreateQuest.Fields.Title.Label)}
                  required
                  maxLength={50}
                />
              </Box>

              <Box sx={{ flex: 3.5 }}>
                <FormSelectInput
                  name="type"
                  control={control}
                  label={t(Resources.Pages.CreateQuest.Fields.Type.Label)}
                  required
                  options={[
                    {
                      key: QuestType.BugHunter,
                      value: t(
                        Resources.Pages.CreateQuest.Fields.Type.Types.BugHunter,
                      ),
                    },
                    {
                      key: QuestType.Mentoring,
                      value: t(
                        Resources.Pages.CreateQuest.Fields.Type.Types.Mentoring,
                      ),
                    },
                    {
                      key: QuestType.Engineering,
                      value: t(
                        Resources.Pages.CreateQuest.Fields.Type.Types
                          .Engineering,
                      ),
                    },
                  ]}
                />
              </Box>
            </Stack>

            <Stack direction="row" spacing={customSpacing.md}>
              <Box sx={{ flex: 6.5 }}>
                <FormNumberInput
                  name="reward"
                  control={control}
                  label={t(Resources.Pages.CreateQuest.Fields.Reward.Label)}
                  required
                  suffix={t(Resources.Pages.CreateQuest.Fields.Reward.Suffix)}
                  maxValue={100000}
                  placeholder="0"
                />
              </Box>

              <Box sx={{ flex: 3.5 }}>
                <FormNumberInput
                  name="maxParticipants"
                  control={control}
                  label={t(
                    Resources.Pages.CreateQuest.Fields.MaxParticipants.Label,
                  )}
                  maxValue={200}
                />
              </Box>
            </Stack>

            <Stack direction="row" spacing={customSpacing.md}>
              <Box sx={{ flex: 1 }}>
                <FormDatePickerInput
                  name="start"
                  control={control}
                  label={t(Resources.Pages.CreateQuest.Fields.Dates.StartLabel)}
                  disablePast={!isStartDateInPast}
                  minDate={isStartDateInPast ? undefined : today}
                  disabled={isStartDateInPast}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <FormDatePickerInput
                  name="expiration"
                  control={control}
                  label={t(
                    Resources.Pages.CreateQuest.Fields.Dates.ExpirationLabel,
                  )}
                  minDate={minExpirationDate}
                />
              </Box>
            </Stack>
          </Stack>
          <Stack
            sx={{
              backgroundColor: colors.background.primary,
              borderRadius: radius.xl,
              p: customSpacing.xl,
              flex: 1,
              minHeight: 0,
            }}
          >
            <FormRichTextInput
              name="description"
              control={control}
              label={t(Resources.Pages.CreateQuest.Fields.Description.Label)}
              maxLength={300}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            />
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
            flexShrink: 0,
            background: "transparent",
          }}
          spacing={customSpacing.md}
        >
          <CloseButton onClick={handleClose} disabled={isPending} />
          <SubmitButton mode={mode} isSubmitting={isPending} />
        </Stack>
      </Stack>
    </SideDrawer>
  );
};
